/**
 * The review page's three non-card screens: the end-of-session summary, the countdown while only
 * delayed "Igen" cards remain, and the empty queue. Each takes focus on its heading when it
 * appears, because the rating buttons that had focus are gone.
 */
import { useEffect, useRef } from "react";
import { Card, Button } from "@client/components/ui";
import { useLang, useT, useTp } from "@client/i18n";
import { formatDate, formatDuration, formatPercent } from "@client/lib/format";
import { againRate, type ReviewSessionData } from "./session";

/** A card title whose inner span takes focus on mount (the heading itself is the card's `h2`). */
function FocusedTitle({ children }: { children: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => ref.current?.focus(), []);
  return (
    <span ref={ref} tabIndex={-1} className="outline-none">
      {children}
    </span>
  );
}

export function SummaryView({
  session,
  pageTitle,
  onExit,
}: {
  session: ReviewSessionData;
  pageTitle: string;
  /** Ends the session and goes to `to`. */
  onExit: (to: string) => void;
}) {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  return (
    <div className="mx-auto max-w-xl">
      <h1 className="sr-only">{pageTitle}</h1>
      <Card title={<FocusedTitle>{t("flashcards.review.summary.title")}</FocusedTitle>}>
        <p className="text-ink-700 dark:text-ink-200">
          {tp("flashcards.review.summary.reviewed", session.reviewed)}
        </p>
        <p className="text-ink-700 dark:text-ink-200">
          {t("flashcards.review.summary.againRate", {
            percent: formatPercent(againRate(session), lang),
          })}
        </p>
        <p className="text-ink-700 dark:text-ink-200">
          {session.nextDueAt === null
            ? t("flashcards.review.summary.noneDue")
            : t("flashcards.review.summary.nextDue", {
                date: formatDate(session.nextDueAt, lang),
              })}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {session.returnTo && (
            <Button onClick={() => onExit(session.returnTo!)}>
              {t("flashcards.review.summary.backToToday")}
            </Button>
          )}
          <Button
            variant={session.returnTo ? "secondary" : "primary"}
            onClick={() => onExit("/flashcards")}
          >
            {t("flashcards.review.summary.backToDecks")}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export function WaitingView({
  pageTitle,
  readyAt,
  nowMs,
  onBack,
}: {
  pageTitle: string;
  readyAt: number | null;
  nowMs: number;
  onBack: () => void;
}) {
  const t = useT();
  return (
    <div className="mx-auto max-w-xl">
      <h1 className="sr-only">{pageTitle}</h1>
      <Card title={<FocusedTitle>{t("flashcards.review.waiting.title")}</FocusedTitle>}>
        <p className="text-ink-700 dark:text-ink-200">
          {t("flashcards.review.waiting.body", {
            time: formatDuration(Math.max(1, (readyAt ?? nowMs) - nowMs)),
          })}
        </p>
        <Button className="mt-4" onClick={onBack}>
          {t("flashcards.review.waiting.backToDecks")}
        </Button>
      </Card>
    </div>
  );
}

/** A mixed review that found nothing to review (or could not be fetched). */
export function EmptyView({
  pageTitle,
  returnTo,
  onGo,
}: {
  pageTitle: string;
  returnTo: string | null;
  /** Just navigates: there is no session of this visit to end. */
  onGo: (to: string) => void;
}) {
  const t = useT();
  return (
    <div className="mx-auto max-w-xl">
      <h1 className="sr-only">{pageTitle}</h1>
      <Card title={<FocusedTitle>{t("flashcards.review.empty.title")}</FocusedTitle>}>
        <p className="text-ink-700 dark:text-ink-200">{t("flashcards.review.empty.body")}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {returnTo && (
            <Button onClick={() => onGo(returnTo)}>
              {t("flashcards.review.summary.backToToday")}
            </Button>
          )}
          <Button variant={returnTo ? "secondary" : "primary"} onClick={() => onGo("/flashcards")}>
            {t("flashcards.review.summary.backToDecks")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
